import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { StoreContext } from '../../../models/store/context';
import { QuoteStatus } from '../../../models/store/quoteStatus';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { saveSession } from '../../../services/session/save';
import {
  getPersonalisedQuoteFlag,
  getQuotePageFlag,
  isPersonalisedQuoteVariant,
  isQuotePageVariant,
  PersonalisedQuoteVariant,
  PosthogFeatureFlags,
} from '@domgen/shared/enums/featureFlags';
import { log } from '@domgen/shared/utils/log';
import posthog from 'posthog-js';

/**
 * Reads the tenant's flag for one quote page experiment and narrows the answer to a known variant.
 * Asking PostHog is also what records the exposure ($feature_flag_called), so each experiment is read at most once per quote.
 *
 * @returns The variant, or undefined when the brand has no flag, the user is outside the rollout,
 * or PostHog returns a variant we don't know (logged, then treated as control).
 */
const getExperimentVariant = <T extends string>(
  flag: PosthogFeatureFlags | undefined,
  isVariant: (value: unknown) => value is T,
): T | undefined => {
  if (!flag) return undefined;

  const flagResponse = posthog.getFeatureFlag(flag);

  if (isVariant(flagResponse)) return flagResponse;

  if (flagResponse) {
    log.warn({
      message:
        'Unrecognised quote page experiment variant from PostHog, falling back to the default layout',
      data: { flag, response: flagResponse },
    });
  }

  return undefined;
};

/**
 * Stores quote response data and computed offer details for quote/confirmation views.
 *
 * Chooses payment method preference for display values and navigates to the quote page.
 * Records which quote page experiments the user is in on `quote.experiments`,
 * then persists the updated context to session storage.
 *
 * @param context Current store context.
 * @param event Event containing quote response data.
 * @param enqueue Store enqueue helpers used for navigation emission.
 * @returns Updated context with quote, confirmation offer data, and GA fields.
 */
export const setQuote = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: QuoteResponse },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: 'setQuote', data: event.data });
  const quote = event.data;

  if (!quote?.offer?.contracts || quote.offer.contracts.length === 0) {
    log.error({ message: 'No contracts found in quote', data: quote });
    enqueue.emit.navigate({ path: AppRoute.ERROR });
    return context;
  }

  const applianceCare = quote?.offer?.contracts[0]?.contractAttributes.monthly_subs;

  const offerData = {
    applianceCare,
    startDate: quote?.offer.contracts[0]?.contractAttributes.effectiveDate?.split('T')[0],
    endDate: quote?.offer.contracts[0]?.contractAttributes.contractEndDate,
    netFirstPaymentAmount:
      applianceCare?.cc?.netFirstPaymentAmount || applianceCare?.ach?.netFirstPaymentAmount,
    netFirstPaymentAmountAfterPromotionalPeriod:
      applianceCare?.cc?.discountProfileAttributes?.netFirstPaymentAmountAfterPromotionalPeriod ||
      applianceCare?.ach?.discountProfileAttributes?.netFirstPaymentAmountAfterPromotionalPeriod,
    discountCode: applianceCare
      ? [
          applianceCare.cc?.discountProfileAttributes?.ccDiscountCodeApplied,
          applianceCare.ach?.discountProfileAttributes?.achDiscountCodeApplied,
        ]
          .filter((code): code is string => !!code && code.toString().trim().length > 0)
          .join(' | ')
      : '',
    offerReference: quote?.offer.offerReference,
  };

  log.debug({ message: 'Computed offer data for quote', data: offerData });

  // Quote page experiments. Reading a flag records exposure, so the experiments are nested:
  // the personalised flag is read first, and the v2 flag only for users who are not in the
  // `personalised` arm. Those users never see page A or radio, so counting them in v2 would
  // dilute its results. Personalised control, absent or unrecognised all still read v2.
  const brandCode = import.meta.env.VITE_BRAND_CODE;
  const quotePageFlag = getQuotePageFlag(brandCode);
  const personalisedQuoteFlag = getPersonalisedQuoteFlag(brandCode);
  const personalisedQuote = getExperimentVariant(personalisedQuoteFlag, isPersonalisedQuoteVariant);
  const quotePageV2 =
    personalisedQuote === PersonalisedQuoteVariant.PERSONALISED
      ? undefined
      : getExperimentVariant(quotePageFlag, isQuotePageVariant);

  // GA carries one experiment at a time; the personalised quote test takes precedence over v2
  // whenever it returned a recognised variant (personalised control included).
  const gaExperiment = personalisedQuote
    ? { experimentName: personalisedQuoteFlag, experimentVariant: personalisedQuote }
    : { experimentName: quotePageV2 ? quotePageFlag : undefined, experimentVariant: quotePageV2 };

  enqueue.emit.navigate({ path: AppRoute.QUOTE });
  const updatedContext = {
    ...context,
    quote: {
      status: QuoteStatus.READY,
      data: quote,
      experiments: { quotePageV2, personalisedQuote },
    },
    confirmation: {
      ...context.confirmation,
      applianceDetails: {
        ...context.confirmation?.applianceDetails,
        guarantee: quote?.items?.[0]?.guarantee ?? 12, // default to 12 months if guarantee length is not available from quote response
      },
      offerData,
    },
    gaEventData: {
      ...context.gaEventData,
      netFirstPaymentAmount: offerData.netFirstPaymentAmount,
      ...gaExperiment,
    },
  };

  saveSession(updatedContext);

  return updatedContext;
};
